import React, { useState, useMemo, useEffect } from "react";
import { roadmap } from "../data/roadmap";
import { FaTimes } from "react-icons/fa";

const STORAGE_KEY = "dev-roadmap-progress";

const Roadmap = ({ onClose }) => {
  const [checkedItems, setCheckedItems] = useState({});
  const [openPhase, setOpenPhase] = useState(null);
  const [openTopic, setOpenTopic] = useState({});

  /* -----------------------------
     Load saved progress
  ------------------------------ */
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) setCheckedItems(JSON.parse(saved));
  }, []);

  /* -----------------------------
     Save progress
  ------------------------------ */
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(checkedItems));
  }, [checkedItems]);

  /* -----------------------------
     Global progress
  ------------------------------ */
  const { totalPoints, checkedPoints } = useMemo(() => {
    let total = 0;
    let checked = 0;

    roadmap.forEach((phase, pIndex) => {
      phase.topics.forEach((topic, tIndex) => {
        topic.points.forEach((_, pointIndex) => {
          total++;
          const key = `${pIndex}-${tIndex}-${pointIndex}`;
          if (checkedItems[key]) checked++;
        });
      });
    });

    return { totalPoints: total, checkedPoints: checked };
  }, [checkedItems]);

  const progress =
    totalPoints === 0 ? 0 : (checkedPoints / totalPoints) * 100;

  /* -----------------------------
     Toggle checkbox
  ------------------------------ */
  const toggleCheck = (p, t, i) => {
    const key = `${p}-${t}-${i}`;
    setCheckedItems((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  /* -----------------------------
     Phase progress
  ------------------------------ */
  const phaseProgress = (pIndex) => {
    let total = 0;
    let checked = 0;

    roadmap[pIndex].topics.forEach((topic, tIndex) => {
      topic.points.forEach((_, pointIndex) => {
        total++;
        if (checkedItems[`${pIndex}-${tIndex}-${pointIndex}`]) checked++;
      });
    });

    return total === 0 ? 0 : Math.round((checked / total) * 100);
  };

  /* -----------------------------
     Topic progress
  ------------------------------ */
  const topicProgress = (pIndex, tIndex) => {
    let total = 0;
    let checked = 0;

    roadmap[pIndex].topics[tIndex].points.forEach((_, pointIndex) => {
      total++;
      if (checkedItems[`${pIndex}-${tIndex}-${pointIndex}`]) checked++;
    });

    return total === 0 ? 0 : Math.round((checked / total) * 100);
  };

  return (
    <div className="fixed inset-0 bg-[#0f172a] text-white z-[9999] overflow-y-auto">

      {/* Background */}
      <div className="fixed inset-0 bg-gradient-to-br from-blue-900/30 via-slate-900 to-black -z-10" />

      {/* HEADER */}
      <div className="sticky top-0 backdrop-blur-xl bg-black/40 border-b border-white/10 z-20">
        <div className="max-w-7xl mx-auto flex justify-between items-center p-6">

          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent">
              My Engineer Master Roadmap
            </h1>
            <p className="text-slate-400 text-sm">
              Track your journey to becoming a world-class engineer
            </p>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 transition"
          >
            <FaTimes size={20} />
          </button>

        </div>

        {/* GLOBAL PROGRESS */}
        <div className="max-w-7xl mx-auto px-6 pb-6">
          <div className="flex justify-between text-sm mb-2 text-slate-400">
            <span>{checkedPoints}/{totalPoints} completed</span>
            <span>{Math.round(progress)}%</span>
          </div>

          <div className="h-3 w-full bg-slate-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      {/* LAYOUT */}
      <div className="max-w-7xl mx-auto grid lg:grid-cols-4 gap-10 py-12 px-6">

        {/* SIDEBAR */}
        <div className="space-y-6">

          <div className="backdrop-blur-lg bg-white/5 border border-white/10 rounded-xl p-6">
            <h3 className="text-lg font-semibold mb-4">Progress</h3>

            <div className="text-5xl font-bold text-blue-400">
              {Math.round(progress)}%
            </div>

            <p className="text-sm text-slate-400 mt-2">
              Stay consistent. Mastery takes time.
            </p>
          </div>

          <div className="backdrop-blur-lg bg-white/5 border border-white/10 rounded-xl p-6">
  <h3 className="text-lg font-semibold mb-4">Weekly Learning Plan</h3>

  <div className="space-y-3 text-sm text-slate-300">
    
    <div>
      <span className="font-semibold text-white">Mon–Thu</span>
      <div>Backend / DSA</div>
    </div>

    <div>
      <span className="font-semibold text-white">Friday</span>
      <div>AI Learning</div>
    </div>

    <div>
      <span className="font-semibold text-white">Saturday</span>
      <div>AI Project Building</div>
    </div>

    <div>
      <span className="font-semibold text-white">Sunday</span>
      <div>Revision + Experiments</div>
    </div>

  </div>
</div>

        </div>

        {/* ROADMAP */}
        <div className="lg:col-span-3 space-y-8">

          {roadmap.map((phase, pIndex) => (

            <div
              key={pIndex}
              className="backdrop-blur-lg bg-white/5 border border-white/10 rounded-2xl shadow-lg"
            >

              {/* PHASE HEADER */}
              <div
                onClick={() =>
                  setOpenPhase(openPhase === pIndex ? null : pIndex)
                }
                className="cursor-pointer p-6 flex justify-between items-center hover:bg-white/5 transition"
              >

                <h2 className="text-lg font-semibold">
                  {phase.phase}
                </h2>

                <div className="text-sm text-cyan-400 font-semibold">
                  {phaseProgress(pIndex)}%
                </div>

              </div>

              {/* PHASE CONTENT */}
              {openPhase === pIndex && (

                <div className="px-6 pb-6 space-y-6">

                  {phase.topics.map((topic, tIndex) => {

                    const topicKey = `${pIndex}-${tIndex}`;

                    return (
                      <div key={tIndex}>

                        {/* TOPIC */}
                        <div
                          onClick={() =>
                            setOpenTopic(prev => ({
                              ...prev,
                              [topicKey]: !prev[topicKey]
                            }))
                          }
                          className="cursor-pointer text-blue-300 font-medium py-2 flex justify-between items-center"
                        >
                          <span>▸ {topic.title}</span>

                          <span className="text-xs text-cyan-400 font-semibold">
                            {topicProgress(pIndex, tIndex)}%
                          </span>
                        </div>

                        {/* POINTS */}
                        {openTopic[topicKey] && (

                          <div className="pl-6 space-y-3">

                            {topic.points.map((point, i) => {

                              const key = `${pIndex}-${tIndex}-${i}`;
                              const checked = checkedItems[key];

                              return (
                                <label
                                  key={i}
                                  className="flex gap-3 items-start text-sm"
                                >

                                  <input
                                    type="checkbox"
                                    checked={checked || false}
                                    onChange={() =>
                                      toggleCheck(pIndex, tIndex, i)
                                    }
                                    className="accent-blue-500 mt-1"
                                  />

                                  <span
                                    className={
                                      checked
                                        ? "line-through text-slate-500"
                                        : "text-slate-200"
                                    }
                                  >
                                    {point}
                                  </span>

                                </label>
                              );
                            })}

                          </div>

                        )}

                      </div>
                    );
                  })}

                </div>

              )}

            </div>

          ))}

        </div>

      </div>

      {progress === 100 && (
        <div className="fixed bottom-10 left-0 right-0 text-center text-green-400 font-bold text-xl animate-pulse">
          🎉 Roadmap Completed – You are production ready!
        </div>
      )}

    </div>
  );
};

export default Roadmap;